import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import invariant from 'tiny-invariant';

import type { CreateOrganisationClientPayload, EuropeanVatNumber } from '~/api/clients/types';
import type { EuVatCountryCode } from '~/api/common/types';

import { useFetchVatInformation } from '~/api/clients';
import { Blocker, Button, Link } from '~/components';
import { Field, Form, Input } from '~/components/Form';
import { europeanUnionVatCountryCodes } from '~/constants/countries';
import { bypassBlocker } from '~/constants/router';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { includes } from '~/utils/arrays';
import { isNotBlank } from '~/utils/string';
import { toast } from '~/utils/toast';

import type {
  CreateEuropeanOrganisationWithVatNumberProps,
  EuropeanOrganisationWithVatNumberFormType,
  CreateEuropeanOrganisationWithVatNumberFormProps as Props,
  SearchEuVatNumberFormType,
} from './types';

import { FormAddressCity } from '../../components/FormAddressCity';
import { FormAddressNumber } from '../../components/FormAddressNumber';
import { FormAddressStreet } from '../../components/FormAddressStreet';
import { FormAddressZipCode } from '../../components/FormAddressZipCode';
import { FormCompanyName } from '../../components/FormCompanyName';
import { mapVatCountryCodeToCountryCode } from '../../utils';
import { useCreateClientContext } from '../context';
import { WizardFooter } from '../WizardFooter/WizardFooter';
import { SwitchNumberType } from './components/SwitchNumberType';
import { VatCountryCodeSelect } from './components/VatCountryCodeSelect';
import { ReadOnlyVatNumberInput, VatNumberInput } from './components/VatNumberInput';
import { useCreateOrganisationContext } from './context';
import { useGetDuplicateClient, useIsBlocked } from './hooks';
import { fieldActionStyle, fullWidthStyle, gridStyle, inputRowStyle, inputStyle, searchRowStyle, stepStyle } from './organisationStep.css';

export const CreateEuropeanOrganisationWithVatNumber = ({ switchToOtherNumber }: CreateEuropeanOrganisationWithVatNumberProps) => {
  const fetchVatInformation = useFetchVatInformation();
  const { organisation, resetClientContext, setOrganisation } = useCreateClientContext();
  const { vatCountryCode } = useCreateOrganisationContext();

  invariant(includes(europeanUnionVatCountryCodes, vatCountryCode), 'Expected selected country to be part of the European Union');

  const navigate = useNavigate();
  const { search } = useLocation();
  const { t } = useTranslation(['clients', 'validation']);

  const { control, handleSubmit, setError } = useForm<SearchEuVatNumberFormType>({
    defaultValues: {
      vatNumberWithoutCountryCode: organisation?.vatNumber?.slice(2) ?? '',
    },
  });

  const isBlocked = useIsBlocked(control);

  const getDuplicateClient = useGetDuplicateClient();

  const onSubmit = handleSubmit((data) => {
    if (fetchVatInformation.isPending) return;

    fetchVatInformation.mutate(`${vatCountryCode}${data.vatNumberWithoutCountryCode}`, {
      onSuccess: ({ vatValid }) => {
        if (vatValid) return;

        setError('vatNumberWithoutCountryCode', { type: 'validVat', message: t('validation:vatNumber.invalid') });
        fetchVatInformation.reset();
      },
      onError: () => {
        toast.danger({ description: t('clients:alerts.errorViesInformation') });
      },
    });
  });

  if ((fetchVatInformation.isSuccess && fetchVatInformation.data.source === 'VIES') || organisation) {
    const { data: vatInformation } = fetchVatInformation;

    if (vatInformation) invariant(vatInformation.vatValid, 'Expected valid VAT number');

    return (
      <CreateEuropeanOrganisationForm
        onCancel={() => {
          fetchVatInformation.reset();
          resetClientContext();
        }}
        onNext={(data) => {
          setOrganisation({ clientType: 'organisation', ...data });
          navigate({ pathname: routes.createClientContactStep, search }, { state: bypassBlocker });
        }}
        organisation={
          vatInformation
            ? {
                vatNumber: vatInformation.vatNumber,
                companyName: vatInformation.companyName,
                address: {
                  street: null,
                  number: null,
                  zipCode: null,
                  city: null,
                  countryCode: mapVatCountryCodeToCountryCode(vatCountryCode),
                },
              }
            : (organisation as CreateOrganisationClientPayload['client'] & { vatNumber: EuropeanVatNumber })
        }
      />
    );
  }

  return (
    <>
      <Blocker isBlocked={isBlocked} message={t('clients:create.blocker')} />

      <Form autoComplete="off" className={stepStyle} onSubmit={onSubmit}>
        <div className={searchRowStyle}>
          <VatCountryCodeSelect />

          <Controller
            control={control}
            name="vatNumberWithoutCountryCode"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label>{t('clients:fields.vatNumber.label')}</Field.Label>

                <div className={inputRowStyle}>
                  <div className={inputStyle}>
                    <VatNumberInput countryCode={vatCountryCode} onBlur={onBlur} onChange={onChange} ref={ref} value={value} />
                  </div>

                  <Button icon="Search" loading={fetchVatInformation.isPending} type="submit">
                    {t('clients:create.company.searchCompanyInformation')}
                  </Button>
                </div>

                <Field.Error match={!!error}>
                  {error?.type === 'unique' ? <DuplicateClientError vatNumberWithoutCountryCode={value} /> : error?.message}
                </Field.Error>

                <SwitchNumberType i18nKey="clients:create.company.noVatNumberLink" onSwitch={switchToOtherNumber} />
              </Field.Root>
            )}
            rules={{
              required: { value: true, message: t('validation:required') },
              validate: {
                notBlank: (value) => isNotBlank(value) || t('validation:required'),
                unique: (value) => !getDuplicateClient(vatCountryCode, value),
              },
            }}
          />
        </div>

        <WizardFooter backTo={{ pathname: routes.createClient, search }}>
          <Button disabled icon="ArrowRight" iconPlacement="right">
            {t('clients:create.next')}
          </Button>
        </WizardFooter>
      </Form>
    </>
  );
};

const DuplicateClientError = ({ vatNumberWithoutCountryCode }: { vatNumberWithoutCountryCode: string }) => {
  const { vatCountryCode } = useCreateOrganisationContext();
  const getDuplicateClient = useGetDuplicateClient();

  const duplicateClient = getDuplicateClient(vatCountryCode, vatNumberWithoutCountryCode);

  return (
    <Trans
      components={[
        // The client may already be gone; the message keeps its text and loses only its link.
        duplicateClient ? (
          <Link key={null} state={bypassBlocker} to={r(routes.createContact, { clientId: duplicateClient.id })} />
        ) : (
          <span key={null} />
        ),
      ]}
      i18nKey="validation:vatNumber.unique"
    />
  );
};

const CreateEuropeanOrganisationForm = ({ onCancel, onNext, organisation }: Props) => {
  const { control, handleSubmit } = useForm<EuropeanOrganisationWithVatNumberFormType>({
    defaultValues: {
      vatNumber: organisation.vatNumber,
      otherNumber: organisation.otherNumber ?? '',
      companyName: organisation.companyName ?? '',
      address: {
        street: organisation.address.street ?? '',
        number: organisation.address.number ?? '',
        zipCode: organisation.address.zipCode ?? '',
        city: organisation.address.city ?? '',
        countryCode: organisation.address.countryCode,
      },
    },
  });

  const { search } = useLocation();
  const { t } = useTranslation(['clients', 'validation']);

  return (
    <>
      <Blocker isBlocked message={t('clients:create.blocker')} />

      <Form autoComplete="off" className={stepStyle} onSubmit={handleSubmit(onNext)}>
        <div className={searchRowStyle}>
          <VatCountryCodeSelect disabled />

          <Field.Root disabled name="vatNumber">
            <Field.Label>{t('clients:fields.vatNumber.label')}</Field.Label>

            <ReadOnlyVatNumberInput
              countryCode={organisation.vatNumber.slice(0, 2) as EuVatCountryCode}
              vatNumber={organisation.vatNumber.slice(2)}
            />

            <div className={fieldActionStyle}>
              <Button emphasis="tertiary" icon="Undo" onClick={onCancel}>
                {t('clients:create.company.changeVatNumber')}
              </Button>
            </div>
          </Field.Root>
        </div>

        <div className={gridStyle}>
          <FormCompanyName control={control} />

          <Controller
            control={control}
            name="otherNumber"
            render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
              <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
                <Field.Label optional>{t('clients:fields.otherNumber.label')}</Field.Label>

                <Input onBlur={onBlur} onValueChange={onChange} ref={ref} value={value ?? ''} />

                <Field.Error match={!!error}>{error?.message}</Field.Error>
              </Field.Root>
            )}
          />

          <FormAddressStreet control={control} />
          <FormAddressNumber control={control} />
          <FormAddressZipCode control={control} />
          <FormAddressCity className={fullWidthStyle} control={control} />
        </div>

        <WizardFooter backTo={{ pathname: routes.createClient, search }}>
          <Button icon="ArrowRight" iconPlacement="right" type="submit">
            {t('clients:create.next')}
          </Button>
        </WizardFooter>
      </Form>
    </>
  );
};
